/**
 * The harness's settings document, kept in this browser.
 *
 * The harness's settings forms (`ui-settings`) read and write a host settings document through two RPCs,
 * `settings/describe` and `settings/mutate`, but only when the page owns its host (`remote.$host.isLoopback`); a
 * hosted page otherwise keeps every choice in memory and loses it on reload. Orik's transport declares that it owns
 * the host (`ownsHost`, `src/lib/agent/harness-transport.ts`) and answers both RPCs here, from one document in
 * `localStorage`. The harness's own plugins then keep their preferences exactly as on their own desktop: the theme
 * and fonts (`ui-theme`), the language, the chat and composer choices, and the developer tools switch.
 *
 * Only the preference namespaces of the client plugins Orik boots are served, each with that plugin's own schema
 * (its package's `Config` and namespace constant), so a stored value is validated and resolved the way the
 * harness's host would. Every other namespace is absent, which the forms read as "not configurable here".
 */
import * as Chat from "@deepseek-ai/dsh-client-ui-chat";
import * as Conversation from "@deepseek-ai/dsh-client-ui-conversation";
import * as Locale from "@deepseek-ai/dsh-client-locale";
import * as Settings from "@deepseek-ai/dsh-client-ui-settings";
import * as Theme from "@deepseek-ai/dsh-client-ui-theme";
import { HARNESS_SETTINGS_KEY, readSettingsDocument, writeSettingsDocument, type SettingsDocument, type UserSection } from "../src/lib/harness-settings";

/** A JSON value, as the settings wire carries it. */
type Json = null | string | number | boolean | Json[] | { [key: string]: Json };

/** A plugin's preference schema: schemastery, called to validate and resolve, serialized for the forms. */
interface Schema {
  (value: unknown): unknown;
  toJSON(): unknown;
}

/**
 * The namespaces this page serves: each client plugin's own schema under its own entry id.
 *
 * `ui-settings` does not export its namespace constant; it is the entry id of `@deepseek-ai/dsh-client-ui-settings`,
 * as `ui-theme` is `@deepseek-ai/dsh-client-ui-theme`'s.
 */
export const SERVED_NAMESPACES: Readonly<Record<string, Schema>> = {
  [Theme.THEME_SETTINGS_NAMESPACE]: Theme.Config as unknown as Schema,
  [Locale.LOCALE_SETTINGS_NAMESPACE]: Locale.Config as unknown as Schema,
  [Chat.CHAT_SETTINGS_NAMESPACE]: Chat.Config as unknown as Schema,
  [Conversation.CONVERSATION_SETTINGS_NAMESPACE]: Conversation.Config as unknown as Schema,
  "ui-settings": Settings.Config as unknown as Schema,
};

/** One namespace as `settings/describe` and `settings/mutate` answer it (`SettingsNamespaceView`). */
export interface NamespaceView {
  autoGenerate: false;
  ns: string;
  schema: Json;
  value: Json;
  base: Json;
  user: Json;
  applies: "live";
  secrets: [];
  revision: number;
}

/** The whole document, as `settings/describe` answers it (`SettingsDescribeValue`). */
export interface DescribeValue {
  writable: true;
  hasDocument: false;
  namespaces: NamespaceView[];
}

/** One path-addressed edit (`SettingsPathOpView`). */
export type PathOp = { op: "set"; path: string[]; value: Json } | { op: "unset"; path: string[] };

/** A refused write, in the RPC failure envelope the harness's connection reads. */
export interface SettingsFailure {
  code: string;
  message: string;
  details: Record<string, Json>;
}

/**
 * A resolved section as plain JSON.
 *
 * The plugins' preference fields are `volatile()`: resolving them yields live cells (`{ get() }`) a host reads as the
 * value changes. The settings wire carries the values themselves.
 */
function plain(value: unknown): Json {
  if (typeof value === "object" && value !== null && !Array.isArray(value)) {
    const cell = value as { get?: unknown };
    if (typeof cell.get === "function" && Object.keys(value).length === 1) return plain((cell.get as () => unknown)());
    return Object.fromEntries(Object.entries(value).map(([key, field]) => [key, plain(field)]));
  }
  if (Array.isArray(value)) return value.map(plain);
  return value as Json;
}

/** Resolve a section through its schema; a stored value the schema refuses resolves as the defaults. */
function resolve(schema: Schema, user: UserSection): { value: Json; user: UserSection } {
  try {
    return { value: plain(schema(user)), user };
  } catch {
    return { value: plain(schema({})), user: {} };
  }
}

function view(ns: string, schema: Schema, document: SettingsDocument): NamespaceView {
  const stored = document.namespaces[ns];
  const { value, user } = resolve(schema, stored?.user ?? {});
  return {
    autoGenerate: false,
    ns,
    schema: schema.toJSON() as Json,
    value,
    base: plain(schema({})),
    user,
    applies: "live",
    secrets: [],
    revision: stored?.revision ?? 0,
  };
}

/** `settings/describe`: every served namespace. */
export function describeSettings(): DescribeValue {
  const document = readSettingsDocument();
  return {
    writable: true,
    hasDocument: false,
    namespaces: Object.entries(SERVED_NAMESPACES).map(([ns, schema]) => view(ns, schema, document)),
  };
}

/** Apply one op to a copy of a section; paths address nested objects, created as needed. */
function applyOp(section: UserSection, op: PathOp): UserSection {
  if (op.path.length === 0) {
    if (op.op === "unset") return {};
    if (typeof op.value !== "object" || op.value === null || Array.isArray(op.value)) throw new Error("the section root must be an object");
    return structuredClone(op.value);
  }
  const next = structuredClone(section);
  let target: Record<string, Json> = next;
  for (const key of op.path.slice(0, -1)) {
    const child = target[key];
    if (typeof child !== "object" || child === null || Array.isArray(child)) {
      if (op.op === "unset") return next;
      target[key] = {};
    }
    target = target[key] as Record<string, Json>;
  }
  const last = op.path[op.path.length - 1] as string;
  if (op.op === "unset") delete target[last];
  else target[last] = structuredClone(op.value);
  return next;
}

/**
 * `settings/mutate`: apply the ops to one namespace's user section, atomically.
 *
 * A stale `expectedRevision` is refused, as the host refuses it, so a form that read an older document re-reads it
 * rather than overwriting a change made in another tab. A result the schema refuses is refused whole.
 */
export function mutateSettings(ns: string, ops: readonly PathOp[], expectedRevision?: number): { ok: true; value: NamespaceView } | { ok: false; error: SettingsFailure } {
  const schema = SERVED_NAMESPACES[ns];
  if (schema === undefined) return { ok: false, error: { code: "settings/unknown-namespace", message: `settings namespace ${JSON.stringify(ns)} is not configurable here`, details: { ns } } };
  const document = readSettingsDocument();
  const stored = document.namespaces[ns] ?? { user: {}, revision: 0 };
  if (expectedRevision !== undefined && expectedRevision !== stored.revision) {
    return { ok: false, error: { code: "settings/revision-conflict", message: "the settings document changed since it was read", details: { ns, expectedRevision, revision: stored.revision } } };
  }
  let user = stored.user;
  try {
    for (const op of ops) user = applyOp(user, op);
    schema(user);
  } catch (error) {
    return { ok: false, error: { code: "settings/invalid", message: error instanceof Error ? error.message : String(error), details: { ns } } };
  }
  document.namespaces[ns] = { user, revision: stored.revision + 1 };
  writeSettingsDocument(document);
  return { ok: true, value: view(ns, schema, document) };
}

/** The theme's resolved section, for the first paint before the harness loads. */
function themeSection(): Record<string, unknown> {
  return resolve(SERVED_NAMESPACES[Theme.THEME_SETTINGS_NAMESPACE] as Schema, readSettingsDocument().namespaces[Theme.THEME_SETTINGS_NAMESPACE]?.user ?? {}).value as Record<string, unknown>;
}

/** The body variables `ui-theme` reads at start for each font role, and the section fields that hold them. */
const FONT_SIZE_VARIABLES = { fontSize: "--dsh-content-font-size", codeFontSize: "--dsh-code-font-size", terminalFontSize: "--dsh-terminal-font-size" } as const;
const FONT_FAMILY_VARIABLES = { textFontFamily: "--dsh-font-family-text", codeFontFamily: "--dsh-font-family-code", terminalFontFamily: "--dsh-font-family-terminal" } as const;

/**
 * Put the stored theme on `<body>` the way the harness's own boot script does, before the harness loads.
 *
 * Their host writes that script into the page it serves (`ui-theme`'s boot injections) from its settings document;
 * Orik serves its own page, so the app does the same from this browser's document. `ui-theme` reads the font
 * variables when it starts, so its first snapshot already carries them, and the boot page has the right palette.
 */
export function applyStoredTheme(): void {
  const section = themeSection();
  const preference = section.preference === "light" || section.preference === "dark" ? section.preference : "system";
  const dark = preference === "dark" || (preference === "system" && matchMedia("(prefers-color-scheme: dark)").matches);
  document.documentElement.dataset.dsThemeSource = preference;
  document.body.toggleAttribute("data-ds-dark-theme", dark);
  for (const [field, variable] of Object.entries(FONT_SIZE_VARIABLES)) {
    if (typeof section[field] === "number") document.body.style.setProperty(variable, `${String(section[field])}px`);
  }
  for (const [field, variable] of Object.entries(FONT_FAMILY_VARIABLES)) {
    if (typeof section[field] === "string" && section[field] !== "") document.body.style.setProperty(variable, section[field]);
  }
}

export { HARNESS_SETTINGS_KEY };
